import { inject, Type } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { isObservable, from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IdValidation } from '../../interfaces/id-validation.interface';
import { ErrorService } from '../../services/error/error.service';

const ERROR_SOURCE = 'urlIdGuard';

interface UrlIdValidationConfig {
  param: string;
  validationService: Type<IdValidation>;
  redirectTo?: string;
}

export const urlIdGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const errorService = inject(ErrorService);
  const config = route.data['urlIdValidation'] as UrlIdValidationConfig | undefined;

  // only validate routes that opt in with config
  if (!config) {
    return true;
  }
  // id being used in url
  const routeValue = route.paramMap.get(config.param);
  if (!routeValue) {
    return router.createUrlTree([config.redirectTo ?? '/']);
  }

  const resolvePromiseOrBoolean = (res: Promise<boolean> | Observable<boolean> | boolean) => {
    return res instanceof Promise ? from(res) : of(res);
  }

  const resolveResult = (res: Promise<boolean> | Observable<boolean> | boolean) =>
    isObservable(res) ? res : resolvePromiseOrBoolean(res);

  try {
    const serviceInstance = inject(config.validationService);
    const result$ = resolveResult(serviceInstance.isValidId(routeValue));

    return result$.pipe(
      map((isValid) => (isValid ? true : router.createUrlTree([config.redirectTo ?? '/']))),
      catchError(() => of(router.createUrlTree([config.redirectTo ?? '/']))),
    );
  } catch (error) {
    errorService.report(error, {
      source: ERROR_SOURCE,
      action: 'canActivate',
      metadata: { param: config.param, redirectTo: config.redirectTo ?? '/' },
    });
    return router.createUrlTree([config.redirectTo ?? '/']);
  }
};
